import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface CurriculumCourse {
  id: string
  title: string
  description: string | null
  status: 'active' | 'archived'
  createdAt: string
}

export interface CurriculumVersion {
  id: string
  courseId: string
  versionNumber: number
  title: string
  status: 'draft' | 'published' | 'archived'
}

export interface CurriculumModule {
  id: string
  courseVersionId: string
  title: string
  sortOrder: number
}

export interface CurriculumLesson {
  id: string
  moduleId: string
  title: string
  description: string | null
  sortOrder: number
}

export interface CurriculumAsset {
  id: string
  lessonId: string
  assetType: 'video' | 'pdf' | 'text'
  title: string
  status: 'active' | 'replaced'
  mimeType: string | null
}

/** drivetrack_admin's curriculum authoring workspace: course/version/module/lesson/asset
 * CRUD, all via migration 064/066's SECURITY DEFINER RPCs (never direct table writes —
 * there are no client INSERT/UPDATE/DELETE policies on any curriculum table). */
export function useCurriculumAdmin() {
  const [courses, setCourses] = useState<CurriculumCourse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCourses = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error: fetchErr } = await supabase
      .from('curriculum_courses')
      .select('id, title, description, status, created_at')
      .order('created_at', { ascending: false })
    if (fetchErr) { setError('Could not load courses.'); setLoading(false); return }
    setCourses((data ?? []).map((r: any) => ({
      id: r.id, title: r.title, description: r.description, status: r.status, createdAt: r.created_at,
    })))
    setLoading(false)
  }, [])

  useEffect(() => { fetchCourses() }, [fetchCourses])

  const createCourse = useCallback(async (title: string, licenceClass: 'G2' | 'G', description: string | null) => {
    const { data, error: rpcErr } = await supabase.rpc('create_curriculum_course', {
      p_title: title, p_licence_class: licenceClass, p_description: description ?? undefined,
    })
    if (rpcErr) return { ok: false, error: rpcErr.message }
    await fetchCourses()
    return { ok: true, courseId: data as string }
  }, [fetchCourses])

  const updateCourse = useCallback(async (courseId: string, title: string, description: string | null) => {
    const { data, error: rpcErr } = await supabase.rpc('update_curriculum_course', {
      p_course_id: courseId, p_title: title, p_description: description ?? undefined,
    })
    const result = data as { ok: boolean; code?: string } | null
    if (rpcErr || !result?.ok) return { ok: false, error: result?.code ?? rpcErr?.message }
    await fetchCourses()
    return { ok: true }
  }, [fetchCourses])

  const setCourseStatus = useCallback(async (courseId: string, status: 'active' | 'archived') => {
    const { data, error: rpcErr } = await supabase.rpc('set_curriculum_course_status', {
      p_course_id: courseId, p_status: status,
    })
    const result = data as { ok: boolean; code?: string } | null
    if (rpcErr || !result?.ok) return { ok: false, error: result?.code ?? rpcErr?.message }
    await fetchCourses()
    return { ok: true }
  }, [fetchCourses])

  return { courses, loading, error, refresh: fetchCourses, createCourse, updateCourse, setCourseStatus }
}

export function useCurriculumVersions(courseId: string | undefined) {
  const [versions, setVersions] = useState<CurriculumVersion[]>([])
  const [loading, setLoading] = useState(true)

  const fetchVersions = useCallback(async () => {
    if (!courseId) return
    setLoading(true)
    const { data } = await supabase
      .from('curriculum_course_versions')
      .select('id, course_id, version_number, title, status')
      .eq('course_id', courseId)
      .order('version_number', { ascending: false })
    setVersions((data ?? []).map((r: any) => ({
      id: r.id, courseId: r.course_id, versionNumber: r.version_number, title: r.title, status: r.status,
    })))
    setLoading(false)
  }, [courseId])

  useEffect(() => { fetchVersions() }, [fetchVersions])

  const createVersion = useCallback(async (title: string, description: string | null) => {
    if (!courseId) return { ok: false, error: 'no_course' }
    const { data, error: rpcErr } = await supabase.rpc('create_curriculum_course_version', {
      p_course_id: courseId, p_title: title, p_description: description ?? undefined,
    })
    if (rpcErr) return { ok: false, error: rpcErr.message }
    await fetchVersions()
    const row = Array.isArray(data) ? data[0] : data
    return { ok: true, versionId: row?.version_id as string }
  }, [courseId, fetchVersions])

  const publishVersion = useCallback(async (versionId: string) => {
    const { error: rpcErr } = await supabase.rpc('publish_course_version', { p_version_id: versionId })
    if (rpcErr) return { ok: false, error: rpcErr.message }
    await fetchVersions()
    return { ok: true }
  }, [fetchVersions])

  const archiveVersion = useCallback(async (versionId: string) => {
    const { error: rpcErr } = await supabase.rpc('archive_course_version', { p_version_id: versionId })
    if (rpcErr) return { ok: false, error: rpcErr.message }
    await fetchVersions()
    return { ok: true }
  }, [fetchVersions])

  return { versions, loading, refresh: fetchVersions, createVersion, publishVersion, archiveVersion }
}

export function useCurriculumModules(versionId: string | undefined) {
  const [modules, setModules] = useState<CurriculumModule[]>([])
  const [loading, setLoading] = useState(true)

  const fetchModules = useCallback(async () => {
    if (!versionId) return
    setLoading(true)
    const { data } = await supabase
      .from('curriculum_version_modules')
      .select('id, course_version_id, title, sort_order')
      .eq('course_version_id', versionId)
      .order('sort_order', { ascending: true })
    setModules((data ?? []).map((r: any) => ({
      id: r.id, courseVersionId: r.course_version_id, title: r.title, sortOrder: r.sort_order,
    })))
    setLoading(false)
  }, [versionId])

  useEffect(() => { fetchModules() }, [fetchModules])

  const createModule = useCallback(async (title: string, description: string | null) => {
    if (!versionId) return { ok: false, error: 'no_version' }
    // RPC param is p_course_version_id, not p_version_id (migration 064) — this call was
    // previously using the wrong name, which PostgREST resolves by exact parameter name,
    // so every "+ Add module" click was silently failing with a function-not-found error.
    const { data, error: rpcErr } = await supabase.rpc('create_curriculum_module', {
      p_course_version_id: versionId, p_title: title, p_description: description ?? undefined,
    })
    if (rpcErr) return { ok: false, error: rpcErr.message }
    await fetchModules()
    return { ok: true, moduleId: data as string }
  }, [versionId, fetchModules])

  const updateModule = useCallback(async (moduleId: string, title: string, description: string | null) => {
    const { data, error: rpcErr } = await supabase.rpc('update_curriculum_module', {
      p_module_id: moduleId, p_title: title, p_description: description ?? undefined,
    })
    const result = data as { ok: boolean; code?: string } | null
    if (rpcErr || !result?.ok) return { ok: false, error: result?.code ?? rpcErr?.message }
    await fetchModules()
    return { ok: true }
  }, [fetchModules])

  const deleteModule = useCallback(async (moduleId: string) => {
    const { data, error: rpcErr } = await supabase.rpc('delete_curriculum_module', { p_module_id: moduleId })
    const result = data as { ok: boolean; code?: string } | null
    if (rpcErr || !result?.ok) return { ok: false, error: result?.code ?? rpcErr?.message }
    await fetchModules()
    return { ok: true }
  }, [fetchModules])

  return { modules, loading, refresh: fetchModules, createModule, updateModule, deleteModule }
}

export function useCurriculumLessons(moduleId: string | undefined) {
  const [lessons, setLessons] = useState<CurriculumLesson[]>([])
  const [loading, setLoading] = useState(true)

  const fetchLessons = useCallback(async () => {
    if (!moduleId) return
    setLoading(true)
    const { data } = await supabase
      .from('curriculum_version_lessons')
      .select('id, module_id, title, description, sort_order')
      .eq('module_id', moduleId)
      .order('sort_order', { ascending: true })
    setLessons((data ?? []).map((r: any) => ({
      id: r.id, moduleId: r.module_id, title: r.title, description: r.description, sortOrder: r.sort_order,
    })))
    setLoading(false)
  }, [moduleId])

  useEffect(() => { fetchLessons() }, [fetchLessons])

  const createLesson = useCallback(async (title: string, description: string | null, estimatedMinutes: number | null, learningObjectives: string | null) => {
    if (!moduleId) return { ok: false, error: 'no_module' }
    const { data, error: rpcErr } = await supabase.rpc('create_curriculum_lesson', {
      p_module_id: moduleId, p_title: title, p_description: description ?? undefined,
      p_estimated_minutes: estimatedMinutes ?? undefined, p_learning_objectives: learningObjectives ?? undefined,
    })
    if (rpcErr) return { ok: false, error: rpcErr.message }
    await fetchLessons()
    return { ok: true, lessonId: data as string }
  }, [moduleId, fetchLessons])

  const updateLesson = useCallback(async (lessonId: string, title: string, description: string | null, estimatedMinutes: number | null, learningObjectives: string | null) => {
    const { data, error: rpcErr } = await supabase.rpc('update_curriculum_lesson', {
      p_lesson_id: lessonId, p_title: title, p_description: description ?? undefined,
      p_estimated_minutes: estimatedMinutes ?? undefined, p_learning_objectives: learningObjectives ?? undefined,
    })
    const result = data as { ok: boolean; code?: string } | null
    if (rpcErr || !result?.ok) return { ok: false, error: result?.code ?? rpcErr?.message }
    await fetchLessons()
    return { ok: true }
  }, [fetchLessons])

  const deleteLesson = useCallback(async (lessonId: string) => {
    const { data, error: rpcErr } = await supabase.rpc('delete_curriculum_lesson', { p_lesson_id: lessonId })
    const result = data as { ok: boolean; code?: string } | null
    if (rpcErr || !result?.ok) return { ok: false, error: result?.code ?? rpcErr?.message }
    await fetchLessons()
    return { ok: true }
  }, [fetchLessons])

  return { lessons, loading, refresh: fetchLessons, createLesson, updateLesson, deleteLesson }
}

export function useCurriculumAssets(lessonId: string | undefined) {
  const [assets, setAssets] = useState<CurriculumAsset[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)

  const fetchAssets = useCallback(async () => {
    if (!lessonId) return
    setLoading(true)
    const { data } = await supabase
      .from('curriculum_lesson_assets')
      .select('id, lesson_id, asset_type, title, status, mime_type')
      .eq('lesson_id', lessonId)
      .eq('status', 'active')
      .order('sort_order', { ascending: true })
    setAssets((data ?? []).map((r: any) => ({
      id: r.id, lessonId: r.lesson_id, assetType: r.asset_type, title: r.title, status: r.status, mimeType: r.mime_type,
    })))
    setLoading(false)
  }, [lessonId])

  useEffect(() => { fetchAssets() }, [fetchAssets])

  // Two-phase upload (migration 064): reserve a draft row + server-generated path,
  // upload the real bytes directly to Storage at that exact path, then finalize —
  // which reads the REAL mime type/size back from storage.objects, never trusting
  // whatever this client claims about the file.
  const uploadAsset = useCallback(async (file: File, assetType: 'pdf' | 'video', title: string) => {
    if (!lessonId) return { ok: false, error: 'no_lesson' }
    setUploading(true)
    try {
      const { data: draft, error: draftErr } = await supabase.rpc('create_curriculum_lesson_asset_draft', {
        p_lesson_id: lessonId, p_asset_type: assetType, p_title: title,
      })
      if (draftErr) return { ok: false, error: draftErr.message }
      const row = Array.isArray(draft) ? draft[0] : draft
      const assetId: string = row?.asset_id
      const storagePath: string = row?.storage_path

      const { error: uploadErr } = await supabase.storage
        .from('curriculum-assets')
        .upload(storagePath, file, { contentType: file.type, upsert: false })
      if (uploadErr) return { ok: false, error: uploadErr.message }

      const { data: finalized, error: finalizeErr } = await supabase.rpc('finalize_curriculum_lesson_asset', {
        p_asset_id: assetId,
      })
      if (finalizeErr) return { ok: false, error: finalizeErr.message }

      await fetchAssets()
      return { ok: true, asset: finalized }
    } finally {
      setUploading(false)
    }
  }, [lessonId, fetchAssets])

  const archiveAsset = useCallback(async (assetId: string) => {
    const { data, error: rpcErr } = await supabase.rpc('archive_curriculum_lesson_asset', { p_asset_id: assetId })
    const result = data as { ok: boolean; code?: string } | null
    if (rpcErr || !result?.ok) return { ok: false, error: result?.code ?? rpcErr?.message }
    await fetchAssets()
    return { ok: true }
  }, [fetchAssets])

  return { assets, loading, uploading, refresh: fetchAssets, uploadAsset, archiveAsset }
}
